import { EVENT_SITES } from '@crossbeam/itly';

import { isEqual } from 'lodash';
import { storeToRefs } from 'pinia';
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute } from 'vue-router';

import { isOnboardingDemoPopulation } from '@/components/onboarding/onboardingDemoReport';

import useDataSharingTracking from '@/composables/useDataSharingTracking';
import usePopulations from '@/composables/usePopulations';
import { SHARING_TYPES, SharingType } from '@/constants/data_shares';
import { isGoogleOrCsvSource } from '@/constants/data_sources';
import { STANDARD_POPULATION_ORDER } from '@/constants/standard_populations';
import {
  useDataRequestsStore,
  useDataSharePresetsStore,
  useDataSharesStore,
  useFlashesStore,
  usePopulationsStore,
} from '@/stores';
import { DataShareItem } from '@/stores/DataSharesStore';
import { Assignments, DataShareAssignment } from '@/types/data_shares';

export type { DataShareItem };

export type PopulationOptionItem = {
  label: string;
  value: number;
  defaultPresetId?: number;
};

export type PopulationToPopulationMap = Record<
  number,
  Record<number, number[]>
>;

type UseSharingSettingsOptions = {
  prefillDefaults?: boolean;
};

export default function (
  partnerId: number | null = null,
  { prefillDefaults }: UseSharingSettingsOptions = {},
) {
  const hasChanges = ref(false);

  const route = useRoute();
  const dataSharesStore = useDataSharesStore();
  const dataSharePresetsStore = useDataSharePresetsStore();
  const { refreshDataRequestsStore } = useDataRequestsStore();
  const { ready, allPresets } = storeToRefs(dataSharePresetsStore);
  const { standardPopulations, customPopsWithStatsAndSharing } = usePopulations(
    partnerId as never,
  );
  const allPopulations = computed(() => [
    ...standardPopulations.value,
    ...customPopsWithStatsAndSharing.value,
  ]);

  // Session key for this editing session
  const sessionKey = dataSharesStore.getSessionKey(partnerId);

  // References to store editing state
  const dataShares = computed({
    get: () => dataSharesStore.editingDataShares[sessionKey] || [],
    set: (value) => {
      dataSharesStore.editingDataShares[sessionKey] = value;
    },
  });

  const initialDataShares = computed(
    () => dataSharesStore.initialEditingDataShares[sessionKey] || [],
  );

  const currentPopulationToPopulation = computed({
    get: () => dataSharesStore.currentPopToPopMap[sessionKey] || {},
    set: (value) => {
      dataSharesStore.currentPopToPopMap[sessionKey] = value;
    },
  });

  const initialPopulationToPopulation = computed(
    () => dataSharesStore.initialPopToPopMap[sessionKey] || {},
  );

  const bulkPayload = computed({
    get: () => dataSharesStore.bulkPayload[sessionKey] || { data_shares: [] },
    set: (value) => {
      dataSharesStore.bulkPayload[sessionKey] = value;
    },
  });

  const shareIdsToDelete = computed({
    get: () => dataSharesStore.shareIdsToDelete[sessionKey] || {},
    set: (value) => {
      dataSharesStore.shareIdsToDelete[sessionKey] = value;
    },
  });

  // Computed getters from store
  const defaultDataSharesMap = computed(() =>
    dataSharesStore.getDefaultDataSharesMap(allPopulations.value),
  );

  const populationOptions = computed(() =>
    allPopulations.value
      .filter((population) => {
        return !dataShares.value.some(
          (dataShare: DataShareItem) =>
            dataShare.populationId === population.id,
        );
      })
      .map((population) => {
        const isGoogleOrCsv = isGoogleOrCsvSource(population.integration_type);
        const parentId = isGoogleOrCsv
          ? population.source_id
          : population.feed_id;
        return {
          label: population.name,
          value: population.id,
          defaultPresetId:
            presetOptions.value[parentId]?.[0]?.items[0]?.value ?? null,
        } as PopulationOptionItem;
      }),
  );

  const prefillPopShares = computed(
    () => prefillDefaults || route.name === 'onboarding_data_sharing',
  );

  const presetOptions = computed(() =>
    dataSharePresetsStore.getPresetOptionsPerFeed(allPresets.value),
  );

  const singleSharePresets = computed(() => {
    return allPresets.value.filter(
      (preset) => preset.preset_type === 'single_share_preset',
    );
  });

  /**
   * Initialize editing session from store
   */
  function initializeEditingSession() {
    dataSharesStore.initializeEditingSession(partnerId, allPopulations.value);

    const populationsStore = usePopulationsStore();
    const hasUserShares = dataShares.value.some(
      (share) =>
        !isOnboardingDemoPopulation(
          populationsStore.getPopulationById(share.populationId),
        ),
    );

    if (!hasUserShares && prefillPopShares.value) {
      initializeDefaultShares();
    }
  }

  function initializeDefaultShares() {
    const populationsStore = usePopulationsStore();
    populationOptions.value
      .filter(
        (option) =>
          !isOnboardingDemoPopulation(
            populationsStore.getPopulationById(option.value),
          ),
      )
      .forEach((option) =>
        createDefaultDataShare({
          label: option.label,
          value: option.value,
          defaultPresetId: option.defaultPresetId,
        }),
      );
  }

  function createDataShare({
    populationId,
    visibility,
    presetId,
  }: {
    populationId: number;
    visibility: SharingType;
    presetId: number | null;
  }) {
    const existingRule = dataSharesStore.outgoingSharingRules.find(
      (rule) =>
        rule.population_id === populationId &&
        rule.partner_org_id === partnerId,
    );

    const { newPresetId } = processNewItem(populationId, visibility, presetId);

    // Remove the population from the local array if it already exists
    dataShares.value = dataShares.value.filter(
      (share) => share.populationId !== populationId,
    );

    // Add the new data share to the local array
    if (visibility !== SHARING_TYPES.HIDDEN) {
      const newRule = {
        id: existingRule?.id ?? 0,
        partner_org_id: partnerId,
        population_id: populationId,
        visibility,
        data_share_presets_id: newPresetId,
        rule_type: 'partner' as const,
        partner_population_id: null,
        source_field_ids: [],
      };
      dataShares.value.push(
        dataSharesStore.toDataShareItem(newRule, allPopulations.value),
      );
    }
  }

  function createDefaultDataShare({
    value: populationId,
    defaultPresetId,
  }: PopulationOptionItem) {
    createDataShare({
      populationId,
      visibility: SHARING_TYPES.SHARING,
      presetId: defaultPresetId ?? null,
    });
  }

  function createCustomDataShare({
    populationId,
    visibility,
    presetId,
  }: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) {
    createDataShare({
      populationId,
      visibility,
      presetId: presetId ?? null,
    });
  }

  function updateDataShare({
    populationId,
    visibility,
    presetId,
  }: {
    populationId: number;
    visibility: SharingType;
    presetId?: number | null;
  }) {
    const { newPresetId } = processNewItem(populationId, visibility, presetId);

    if (visibility === SHARING_TYPES.HIDDEN && !prefillDefaults) {
      dataShares.value = dataShares.value.filter(
        (share) => share.populationId !== populationId,
      );
    } else {
      const index = dataShares.value.findIndex(
        (share) => share.populationId === populationId,
      );
      dataShares.value[index] = {
        ...dataShares.value[index],
        visibility,
        presetId: newPresetId,
      } as DataShareItem;
    }
  }

  function resetDataShare({ id: shareId, populationId }: DataShareItem) {
    // delete pop from update if present in payload
    bulkPayload.value.data_shares = bulkPayload.value.data_shares.filter(
      (share) => share.population_id !== populationId,
    );
    // add rule id and pop to deletion list
    if (shareId) {
      if (!shareIdsToDelete.value[populationId]) {
        shareIdsToDelete.value[populationId] = [];
      }
      shareIdsToDelete.value[populationId].push(Number(shareId));
    }
    if (currentPopulationToPopulation.value[populationId]) {
      const partnerPopRules =
        dataSharesStore.outgoingSharingRuleMap?.partner_population;

      if (partnerPopRules) {
        Object.keys(partnerPopRules).forEach((partnerPopId) => {
          const rules = partnerPopRules[Number(partnerPopId)];

          if (rules) {
            rules.forEach((rule) => {
              if (
                rule.population_id === populationId &&
                rule?.partner_population_id
              ) {
                deleteOverride(
                  rule.id,
                  rule.partner_population_id,
                  rule.population_id,
                );
              }
            });
          }
        });
      }
    }
    const defaultRule = defaultDataSharesMap.value?.[populationId];
    if (!defaultRule) {
      dataShares.value = dataShares.value.filter(
        (share) => share.populationId !== populationId,
      );
    } else {
      const existingIndex = dataShares.value.findIndex(
        (share) => share.populationId === populationId,
      );
      if (existingIndex !== -1) {
        dataShares.value = dataShares.value.map((share) =>
          share.populationId === populationId ? defaultRule : share,
        );
      } else {
        dataShares.value = [...dataShares.value, defaultRule];
      }
    }
  }

  function deleteOverride(
    shareId: number | null,
    partnerPopulationId: number,
    populationId: number,
  ) {
    dataSharesStore.deletePopToPopOverride(
      partnerId,
      shareId,
      partnerPopulationId,
      populationId,
    );
  }

  async function bulkUpdateDataShares() {
    // Capture shares being saved for tracking
    const savedShares = [...bulkPayload.value.data_shares];

    // Build previous state map for tracking
    const previousStateMap = new Map<number, DataShareItem>();
    savedShares.forEach((share) => {
      const previousShare =
        defaultDataSharesMap.value[share.population_id] ||
        dataSharesStore.outgoingSharingRules.find(
          (rule) =>
            rule.population_id === share.population_id &&
            rule.partner_org_id === partnerId,
        );
      if (previousShare) {
        if ('populationId' in previousShare) {
          previousStateMap.set(share.population_id, previousShare);
        } else {
          previousStateMap.set(
            share.population_id,
            dataSharesStore.toDataShareItem(
              previousShare,
              allPopulations.value,
            ),
          );
        }
      }
    });

    // Delegate to store for actual save operations
    try {
      await dataSharesStore.bulkSaveChanges(partnerId);
      await refreshDataRequestsStore();
      showSuccessToast();
      initializeEditingSession();
    } catch (error) {
      const flashesStore = useFlashesStore();
      const errors = (error as { errors?: string[] }).errors || [
        'An error occurred while saving settings',
      ];
      errors.forEach((err) => flashesStore.addErrorFlash({ message: err }));
      throw error;
    }

    // Track changes for analytics
    savedShares.forEach((share) => {
      const population = allPopulations.value.find(
        (p) => p.id === share.population_id,
      );
      const partnerPopulationMap =
        currentPopulationToPopulation.value[share.population_id];
      const partnerPopulationIds = partnerPopulationMap
        ? Object.keys(partnerPopulationMap).map((id) => Number(id))
        : [];
      const previousDataShareItem = previousStateMap.get(share.population_id);

      trackDataSharingChangeFromContext({
        population: population ?? undefined,
        current: {
          visibility: share.visibility,
          presetId: share.preset_id ?? null,
        },
        previous: previousDataShareItem
          ? {
              visibility: previousDataShareItem.visibility,
              presetId: previousDataShareItem.presetId,
            }
          : undefined,
        eventSite: EVENT_SITES.SHARING_SETTINGS_MODAL,
        bulk: false,
        partnerId: partnerId ?? undefined,
        partnerPopulationIds,
      });
    });
  }

  function processNewItem(
    populationId: number,
    visibility: SharingType,
    presetId?: number | null,
  ) {
    const population = allPopulations.value.find((p) => p.id === populationId);
    const isGoogleOrCsv = isGoogleOrCsvSource(population.integration_type);
    const parentId = isGoogleOrCsv ? population.source_id : population.feed_id;

    const recommendedPresetId = presetOptions.value[parentId]?.[0]?.items[0]
      ?.value as number;

    const newPresetId =
      visibility === SHARING_TYPES.OLAPS_ONLY ||
      visibility === SHARING_TYPES.HIDDEN
        ? null
        : (presetId ?? recommendedPresetId ?? null);

    const newItem = {
      population_id: populationId,
      visibility,
      preset_id: newPresetId,
    };

    addOrReplaceDataShare(bulkPayload.value, populationId, newItem);

    // Remove population from the shares to delete
    shareIdsToDelete.value = Object.fromEntries(
      Object.entries(shareIdsToDelete.value).filter(
        ([id]) => Number(id) !== populationId,
      ),
    );

    return { newPresetId };
  }

  // Track changes between current and initial state
  watch(
    [dataShares, currentPopulationToPopulation],
    () => {
      // Compare only the meaningful fields (visibility and presetId) to avoid
      // false positives when internal fields change during editing
      const hasDataShareChanges = (() => {
        const sortedDataShares = [...dataShares.value].sort(sortPopulations);
        const sortedInitialDataShares = [...initialDataShares.value].sort(
          sortPopulations,
        );

        if (sortedDataShares.length !== sortedInitialDataShares.length) {
          return true;
        }

        return sortedDataShares.some((current, index) => {
          const initial = sortedInitialDataShares[index];
          // Compare only the meaningful fields that represent user's intent
          return (
            current.populationId !== initial?.populationId ||
            current.visibility !== initial?.visibility ||
            current.presetId !== initial?.presetId
          );
        });
      })();

      const hasPopulationToPopulationChanges = !isEqual(
        currentPopulationToPopulation.value,
        initialPopulationToPopulation.value,
      );
      hasChanges.value =
        hasDataShareChanges || hasPopulationToPopulationChanges;
    },
    { deep: true, immediate: true },
  );

  // Watch to avoid a bug:
  // If user changed the current selected preset (A) with an other preset (B)
  // B became now the selected preset (but in the frontend only without saving)
  // If he deletes B (still without saving), B was still visually selected
  // Even tho correctly removes from the preset option list
  // This watch makes sure A is selected again after B deletion
  watch(
    allPresets,
    () => {
      dataShares.value.forEach((share) => {
        const defaultShare = defaultDataSharesMap.value[share.populationId];
        if (
          share.presetId &&
          !allPresets.value.some((p) => p.id === share.presetId) &&
          defaultShare
        ) {
          updateDataShare({
            populationId: share.populationId,
            visibility: share.visibility,
            presetId: defaultShare.presetId,
          });
        }
      });
    },
    { deep: true },
  );

  onMounted(() => {
    if (ready.value && dataSharesStore.ready) {
      initializeEditingSession();
    } else {
      const stop = watch(
        [ready, () => dataSharesStore.ready],
        ([presetsReady, storeReady]) => {
          if (presetsReady && storeReady) {
            initializeEditingSession();
            stop();
          }
        },
      );
    }
  });

  onUnmounted(() => {
    dataSharesStore.cleanupEditingSession(partnerId);
  });

  const { trackDataSharingChangeFromContext } = useDataSharingTracking();

  return {
    isLoading: computed(() => !dataSharesStore.ready || !ready.value),
    dataShares: computed(() => dataShares.value.sort(sortPopulations)),
    hasChanges: computed(() => hasChanges.value),
    populationOptions,
    presetOptions,
    defaultDataSharesMap,
    singleSharePresets,
    createDefaultDataShare,
    createCustomDataShare,
    updateDataShare,
    resetDataShare,
    deleteOverride,
    bulkUpdateDataShares,
    showSuccessToast,
    currentPopulationToPopulation,
    initialPopulationToPopulation,
  };
}

// Helpers

function showSuccessToast() {
  const flashesStore = useFlashesStore();
  flashesStore.addSuccessFlash({ message: 'Settings saved' });
}

function addOrReplaceDataShare(
  payload: Assignments,
  populationId: number,
  newItem: DataShareAssignment,
) {
  const index = payload.data_shares.findIndex(
    (item) => item.population_id === populationId,
  );
  index !== -1
    ? (payload.data_shares[index] = newItem)
    : payload.data_shares.push(newItem);
}

function sortPopulations(a: DataShareItem, b: DataShareItem) {
  const aIndex = STANDARD_POPULATION_ORDER.indexOf(a.populationName);
  const bIndex = STANDARD_POPULATION_ORDER.indexOf(b.populationName);

  if (aIndex !== -1 && bIndex !== -1) {
    return aIndex - bIndex;
  }
  if (aIndex !== -1) return -1;
  if (bIndex !== -1) return 1;

  if (!a.populationName && !b.populationName) return 0;
  if (!a.populationName) return 1;
  if (!b.populationName) return -1;
  return a.populationName.localeCompare(b.populationName);
}
